/**
 * Compact progress state for a chat composer, derived from a session's live
 * status + message stream.
 *
 * Surfaces a single, glanceable state — a "Thinking for Ns…" timer while the
 * agent reasons, otherwise the latest activity line, and a plain "Completed"
 * once the run finishes (the sticky `currentActivity` is ignored on completion).
 * Used by both the canvas floating chat and the workspace floating chat to pulse
 * their FAB launchers while a run is in flight — it has no canvas coupling.
 */

import { useFlowMessages } from "@/api/messages";
import { useFlowSessionLiveStatus } from "@/hooks/useFlowSessionLiveStatus";
import type { Message } from "@cohesive/api-client";
import { useEffect, useMemo, useState } from "react";

export type ComposerProgressState =
  | { kind: "idle" }
  | { kind: "thinking"; seconds: number }
  | { kind: "activity"; label: string }
  | { kind: "completed" }
  | { kind: "error" };

export interface ComposerProgress {
  state: ComposerProgressState;
  /** True while the agent is actively working (thinking/activity). */
  isRunning: boolean;
}

type Phase =
  | { phase: "idle" }
  | { phase: "thinking"; startedAt: number }
  | { phase: "activity"; label: string }
  | { phase: "completed" }
  | { phase: "error" };

function findLast(
  messages: Message[],
  predicate: (m: Message) => boolean,
): Message | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (predicate(messages[i])) return messages[i];
  }
  return undefined;
}

export function useComposerProgress(
  flowSessionId: string | null,
): ComposerProgress {
  const live = useFlowSessionLiveStatus(flowSessionId ?? undefined);
  const { data: messages } = useFlowMessages(flowSessionId ?? "", undefined, {
    enabled: !!flowSessionId,
  });

  // messages are oldest→newest (newest last).
  const phase = useMemo<Phase>(() => {
    const msgs = messages ?? [];
    if (!flowSessionId || msgs.length === 0) return { phase: "idle" };

    if (live.status === "error") return { phase: "error" };

    const lastMsg = msgs[msgs.length - 1];
    const lastAssistant = findLast(
      msgs,
      (m) => m.origin === "assistant" || m.origin === "assistant-tool-call",
    );

    // 1. Reasoning tokens streaming → "Thinking for Ns…" count-up.
    if (
      lastAssistant &&
      lastAssistant.thinking_content &&
      lastAssistant.thinking_duration_milliseconds == null &&
      lastAssistant.status === "streaming"
    ) {
      const startedAt = Date.parse(
        lastAssistant.created_at || lastAssistant.sent_at,
      );
      return {
        phase: "thinking",
        startedAt: Number.isNaN(startedAt) ? Date.now() : startedAt,
      };
    }

    // 2. A specific tool/activity line, if the live hook synthesized one.
    // The live hook falls back to the *session name* (the chat title) while processing
    // when there's no real tool action — don't surface that; let it fall through to the
    // generic "Planning next steps…" below.
    if (
      live.currentActivity &&
      live.status !== "complete" &&
      live.currentActivity !== live.flowSession?.name
    ) {
      return { phase: "activity", label: live.currentActivity };
    }

    // 3. The assistant is streaming its answer.
    if (lastAssistant && lastAssistant.status === "streaming") {
      return { phase: "activity", label: "Responding…" };
    }

    // 4. The user just spoke and the agent hasn't produced anything yet.
    if (lastMsg && lastMsg.origin === "user") {
      return { phase: "activity", label: "Considering…" };
    }

    // 5. Generic in-progress (session busy / tool result pending).
    if (
      live.status === "processing" ||
      live.status === "needs_attention" ||
      (lastAssistant &&
        (lastAssistant.status === "pending" || lastAssistant.status === "done"))
    ) {
      return { phase: "activity", label: "Planning next steps…" };
    }

    // 6. Settled.
    if (live.status === "complete" || lastAssistant?.status === "processed") {
      return { phase: "completed" };
    }

    return { phase: "idle" };
  }, [flowSessionId, messages, live.status, live.currentActivity]);

  // Tick a clock only while thinking so "Thinking for Ns…" counts up.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (phase.phase !== "thinking") return;
    setNowMs(Date.now());
    const id = window.setInterval(() => setNowMs(Date.now()), 500);
    return () => window.clearInterval(id);
  }, [phase.phase]);

  const state = useMemo<ComposerProgressState>(() => {
    switch (phase.phase) {
      case "thinking":
        return {
          kind: "thinking",
          seconds: Math.max(0, Math.floor((nowMs - phase.startedAt) / 1000)),
        };
      case "activity":
        return { kind: "activity", label: phase.label };
      case "completed":
        return { kind: "completed" };
      case "error":
        return { kind: "error" };
      default:
        return { kind: "idle" };
    }
  }, [phase, nowMs]);

  const isRunning = state.kind === "thinking" || state.kind === "activity";

  return { state, isRunning };
}
